import { z } from 'zod';
import { toast } from 'sonner';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Icon } from '@iconify/react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUpdateProcess } from '@/hooks/process/use-process';
import { useGetAllProcessFolders } from '@/hooks/process-folder/use-process-folder';
import type { ProcessFolder } from '@/types/process-folder.types';

const moveProcessSchema = z.object({
  targetFolderId: z.string().min(1, 'Please select a target folder'),
});

type MoveProcessData = z.infer<typeof moveProcessSchema>;

interface MoveProcessModalProps {
  isOpen: boolean;
  onClose: () => void;
  processId: string;
  processName: string;
  currentFolderId?: string | null;
  onSuccess?: () => void;
}

export const MoveProcessModal: React.FC<MoveProcessModalProps> = ({
  isOpen,
  onClose,
  processId,
  processName,
  currentFolderId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const updateProcessMutation = useUpdateProcess();
  const { data: foldersData, isPending: isFoldersPending } = useGetAllProcessFolders();

  const form = useForm<MoveProcessData>({
    resolver: zodResolver(moveProcessSchema),
    defaultValues: {
      targetFolderId: '',
    },
  });

  const onSubmit = async (data: MoveProcessData) => {
    try {
      setIsSubmitting(true);
      
      // If the target folder is the same as current folder, don't do anything
      if (data.targetFolderId === currentFolderId) {
        toast.info(t('common.noChangesToSave'));
        onClose();
        return;
      }

      // Update the process with new processFolderId
      await updateProcessMutation.mutateAsync({
        processId,
        processData: {
          processFolderId: data.targetFolderId,
        } as any, // Type assertion because we're only updating processFolderId
      });
      
      toast.success(t('processManagement.processMovedSuccessfully'));
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error moving process:', error);
      toast.error(t('common.operationFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  // Filter out the current folder from the list
  const availableFolders = foldersData?.filter(folder => folder.id !== currentFolderId) || [];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon icon="material-symbols:drive-file-move" className="text-xl" />
            {t('processManagement.moveProcess')}
          </DialogTitle>
          <DialogDescription>
            {t('processManagement.moveProcessDescription', { processName })}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="targetFolderId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('processManagement.selectTargetFolder')}</FormLabel>
                  <FormControl>
                    <Select
                      value={field.value || ''}
                      onValueChange={field.onChange}
                      disabled={isSubmitting || isFoldersPending}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('processManagement.selectFolder')} />
                      </SelectTrigger>
                      <SelectContent>
                        {availableFolders.map((folder: ProcessFolder) => (
                          <SelectItem key={folder.id} value={folder.id}>
                            <div className="flex items-center gap-2">
                              <Icon icon="material-symbols:folder" className="text-sm" />
                              {folder.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || updateProcessMutation.isPending}
                className="main-dark-button"
              >
                {isSubmitting || updateProcessMutation.isPending ? (
                  <>
                    <Icon icon="eos-icons:loading" className="mr-2" />
                    {t('processManagement.moving')}
                  </>
                ) : (
                  <>
                    <Icon icon="material-symbols:drive-file-move" className="mr-2" />
                    {t('processManagement.move')}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

